import * as React from "react";
import { useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { isPermitted } from "util/PermissionsMixin";
import useCurrentUser from "hooks/useCurrentUser";
import { Timestamp, Switch } from "components/common";
import { GLCheckbox, ConfirmDialog } from "common/components";
import { EmptyList, TD } from "security-app/components/common";
import { usePagination, useSelectedRows, useSelectedRowsDispatch, useModalContext } from "common/contexts";
import { useToggleSigmaRule } from "security-app/hooks/useSigmaAPI";
import usePluggableEntityTableElements from "hooks/usePluggableEntityTableElements";
import RowActions from "./RowActions";
const RuleLevel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  text-transform: capitalize;
`;
const LevelIndicator = styled.div(
  ({ $levelColor }) => css`
    display: inline-block;
    width: 13px;
    height: 13px;
    border-radius: 100px;
    color: #fff;
    text-align: center;
    font-weight: bold;
    line-height: 0.85;

    background-color: ${$levelColor};
    filter: saturate(0.7) brightness(1.3);
  `
);
const defaultNoSearchResultText = "No Rules found for the provided criteria";
function ListRows({
  rules,
  visibleColumns,
  renderRowTitle,
  displayRowActions = true,
  noSearchResultText = defaultNoSearchResultText
}) {
  const { colors } = useTheme();
  const { toggleSigmaRule } = useToggleSigmaRule();
  const { setModal, setEntity } = useModalContext();
  const [ruleToBeToggled, setRuleToBeToggled] = useState();
  const { getPluggableTableCells } = usePluggableEntityTableElements(null, "sigma_rule");
  const localPagination = usePagination();
  const selectedRules = useSelectedRows();
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const levelColorMap = {
    critical: colors.variant.danger,
    high: colors.variant.danger,
    medium: "#F98A3A",
    low: colors.variant.success,
    informational: colors.variant.info
  };
  const { permissions } = useCurrentUser();
  const canManageRule = (rule) => isPermitted(permissions, `sigma_rules:edit:${rule.id}`);
  const onSelect = (rule) => (e) => {
    if (e.target.checked) {
      selectedRulesDispatch({ type: "add", payload: [rule] });
    } else {
      selectedRulesDispatch({ type: "remove", payload: [rule] });
    }
  };
  const isSelected = (rule) => !!selectedRules.find((sRule) => rule.id === sRule.id);
  const editRule = (rule) => {
    setEntity(rule);
    setModal("MANUAL-ADD");
  };
  const toggleRule = (rule = ruleToBeToggled) => {
    toggleSigmaRule(
      { ruleId: rule.id, enable: !rule.enabled },
      { onSuccess: () => selectedRulesDispatch({ type: "update", payload: rule }) }
    );
    setRuleToBeToggled(void 0);
  };
  const onToggleRule = (rule) => {
    if (rule.is_hunting_query && !rule.enabled) {
      setRuleToBeToggled(rule);
    } else {
      toggleRule(rule);
    }
  };
  const columnIsVisible = (columnName) => visibleColumns.includes(columnName);
  return rules.length < 1 ? /* @__PURE__ */ React.createElement(
    EmptyList,
    {
      query: localPagination.query,
      filters: localPagination.filters,
      noSearchResultText,
      noEntitiesExist: "Currently there are no Rules to list",
      colSpan: 8
    }
  ) : /* @__PURE__ */ React.createElement(React.Fragment, null, rules.map((rule) => /* @__PURE__ */ React.createElement("tr", { key: `rule-row-${rule.id}`, "data-testid": "rule-row" }, canManageRule(rule) ? /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(GLCheckbox, { checked: isSelected(rule), onChange: onSelect(rule), "data-testid": "rule-checkbox" })) : (
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    /* @__PURE__ */ React.createElement("td", null)
  ), columnIsVisible("title") && /* @__PURE__ */ React.createElement(TD, { "data-testid": "rule-name" }, renderRowTitle(rule, editRule)), columnIsVisible("level") && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RuleLevel, null, /* @__PURE__ */ React.createElement(LevelIndicator, { $levelColor: levelColorMap[rule.level] }, rule.level === "critical" ? "!" : ""), /* @__PURE__ */ React.createElement("div", null, rule.level))), columnIsVisible("status") && /* @__PURE__ */ React.createElement("td", { style: { textTransform: "capitalize" } }, rule.status), columnIsVisible("last_run") && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: rule.last_run })), columnIsVisible("updated_at") && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: rule.updated_at })), columnIsVisible("enabled") && /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(
    Switch,
    {
      "aria-label": `${rule.enabled ? "Disable" : "Enable"} rule`,
      onChange: () => onToggleRule(rule),
      checked: rule.enabled,
      className: "center",
      disabled: !canManageRule(rule)
    }
  )), getPluggableTableCells(rule.id), canManageRule(rule) && displayRowActions ? /* @__PURE__ */ React.createElement("td", null, /* @__PURE__ */ React.createElement(RowActions, { rule, editRule })) : (
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    /* @__PURE__ */ React.createElement("td", null)
  ), (ruleToBeToggled == null ? void 0 : ruleToBeToggled.id) === rule.id && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: true,
      onConfirm: () => toggleRule(),
      confirmText: "Confirm",
      onCancel: () => setRuleToBeToggled(void 0),
      title: "Are you sure?",
      type: "warning"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, 'Warning! The rule you are attempting to enable is designated as a "hunting query". These rules are typically designed to be run on an ad-hoc basis via the "Search Logs" feature, rather than enabled for background detection. Running rules such as this will typically generate many false positive events, and could possibly cause performance issues.'), /* @__PURE__ */ React.createElement("p", null, "Are you sure you would like to proceed?"))
  ))));
}
export default ListRows;
